import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useApolloClient } from '@vue/apollo-composable';

import { isSignedIn as isSignedInUser } from '@ohmybible/core';
import { AppMeDocument } from '@ohmybible/graphql';

import type { CurrentUser } from '@/shared/types/auth';

/**
 * The signed-in user. The API answers `me` with the guest (`ombu:guest`) when
 * there is no session, so "signed in" is "a user that is not the guest" —
 * readers (no role) and staff alike — by the exact id, as the API and the
 * admin decide it (`isSignedIn` in `@ohmybible/core`).
 */
export const useAuthStore = defineStore('auth', () => {
  const { client } = useApolloClient();

  const user = ref<CurrentUser | null>(null);

  const isSignedIn = computed(() => isSignedInUser(user.value));

  const fetchMe = async (): Promise<void> => {
    const result = await client.query({
      query: AppMeDocument,
      fetchPolicy: 'network-only',
    });

    user.value = result.data?.me ?? null;
  };

  return {
    user,
    isSignedIn,
    fetchMe,
  };
});
